import { SrSalesOrderType } from '@plato/data/starrocks/types';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { LoadingBlockFullPage } from '../../../components/loading/loading-block-full-page.tsx';
import { TableHeader } from '../../../components/tables/table-header.tsx';
import { CopyButton } from '../../../components/ui/buttons/copy-button.tsx';
import { Card } from '../../../components/ui/card.tsx';
import { CollapsibleContainer } from '../../../components/ui/collapsible-container.tsx';
import { DrawerContainer, DrawerContent, DrawerHeader } from '../../../components/ui/drawer.tsx';
import { TabsHeader } from '../../../components/ui/tabs-header.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { ExplorerTopLevelEntity } from '../../../server/utils/explorer-utils.ts';
import { api } from '../../../utils/api.ts';
import { useAppConfig } from '../../acl/acl-provider.tsx';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { ExplorerView } from '../../explorer/components/explorer-view.tsx';
import { ExplorerViewProvider, type ExplorerState } from '../../explorer/hooks/use-explorer-query-params.ts';
import { useOrderColumnsForProductDetail } from '../../list-views/columns/use-order-columns-for-product-detail.tsx';
import { useQuoteColumnsForProductDetail } from '../../list-views/columns/use-quote-columns-for-product-detail.tsx';
import { DataTable } from '../../list-views/components/data-table.tsx';
import { InventoryActions } from './inventory-actions.tsx';
import { InventoryDetailsAvailability } from './inventory-details-availability.tsx';
import { InventoryDetailsDescription } from './inventory-details-description.tsx';
import { InventoryDetailsInfo } from './inventory-details-info.tsx';
import { InventoryDetailsPricingHistory } from './inventory-details-pricing-history.tsx';
import { InventoryDetailsRecommendations } from './inventory-details-recommendations.tsx';
import { InventoryDetailsVariants } from './inventory-details-variants.tsx';

enum PageTabs {
    INFO = 'info',
    AVAILABILITY = 'availability',
    OPEN_QUOTES = 'quotes',
    OPEN_ORDERS = 'orders',
    EXPLORER = 'explorer',
}

type Props = {
    className?: string;
    erpProductId: string;
    customerId?: string;
    onClose?: () => void;
    onBack?: () => void;
};

const InventoryDetails = ({ erpProductId, onBack, onClose, className, customerId }: Props): ReactNode => {
    const { t } = useLocale();
    const { canReadOpenOrders, canReadQuotes, canReadProductVariants, enablePricingDiscovery } =
        useAppConfig();
    const [selectedTab, setSelectedTab] = useState<PageTabs>(PageTabs.INFO);
    const [state, setState] = useState<Partial<ExplorerState>>({
        tab: ExplorerTopLevelEntity.CUSTOMER,
    });

    const sendEvent = useAnalytics();

    useEffect(() => {
        sendEvent('item_detail_page_viewed', {
            item_id: erpProductId,
        });
    }, [erpProductId, sendEvent]);

    const ordersColumns = useOrderColumnsForProductDetail();
    const quotesColumns = useQuoteColumnsForProductDetail();

    /* ============================================
     *  API Calls
     * ============================================
     */
    const {
        data: productDetails,
        isLoading: isLoadingDetail,
        isFetched: isFetchedDetail,
    } = api.platoUser.getDetailProduct.getProductDetail.useQuery({
        erpProductId,
    });

    const { data: pricingSummary, isLoading: isLoadingPricingSummary } =
        api.platoUser.getDetailProduct.getPricingSummary.useQuery(
            { productVariantId: erpProductId },
            { enabled: enablePricingDiscovery },
        );

    const {
        data: ordersRawData,
        isLoading: isLoadingOrders,
        isFetchingNextPage: isFetchingOrdersNextPage,
        fetchNextPage: fetchNextPageOrders,
        isFetched: isFetchedOrders,
        hasNextPage: hasNextPageOrders,
    } = api.platoUser.getManyQuotesOrdersContainingProduct.getListWithFilters.useInfiniteQuery(
        {
            erpProductId: erpProductId,
            orderTypes: [SrSalesOrderType.ORDER],
        },
        {
            enabled: selectedTab === PageTabs.OPEN_ORDERS,
            getNextPageParam: (lastPage) => lastPage.nextCursor,
        },
    );

    const { data: ordersCount, isLoading: isLoadingOrdersCount } =
        api.platoUser.getManyQuotesOrdersContainingProduct.getCount.useQuery(
            {
                erpProductId: erpProductId,
                orderTypes: [SrSalesOrderType.ORDER],
            },
            {
                enabled: isFetchedOrders,
            },
        );

    const {
        data: quotesRawData,
        isLoading: isLoadingQuotes,
        isFetchingNextPage: isFetchingQuotesNextPage,
        fetchNextPage: fetchNextPageQuotes,
        hasNextPage: hasNextPageQuotes,
        isFetched: isFetchedQuotes,
    } = api.platoUser.getManyQuotesOrdersContainingProduct.getListWithFilters.useInfiniteQuery(
        {
            erpProductId: erpProductId,
            orderTypes: [SrSalesOrderType.QUOTE],
        },
        {
            enabled: selectedTab === PageTabs.OPEN_QUOTES,
            getNextPageParam: (lastPage) => lastPage.nextCursor,
        },
    );

    const { data: quotesCount, isLoading: isLoadingQuotesCount } =
        api.platoUser.getManyQuotesOrdersContainingProduct.getCount.useQuery(
            {
                erpProductId: erpProductId,
                orderTypes: [SrSalesOrderType.QUOTE],
            },
            {
                enabled: isFetchedQuotes,
            },
        );

    /* ============================================
     *  Clean Data
     * ============================================
     */

    const quotesData = useMemo(() => {
        if (quotesRawData) {
            return quotesRawData.pages.flatMap((page) => page.result);
        } else {
            return [];
        }
    }, [quotesRawData]);

    const ordersData = useMemo(() => {
        if (ordersRawData) {
            return ordersRawData.pages.flatMap((page) => page.result);
        } else {
            return [];
        }
    }, [ordersRawData]);

    /* ============================================
     *  Tables
     * ============================================
     */

    const quotesTable = useReactTable({
        data: quotesData,
        columns: quotesColumns,
        getCoreRowModel: getCoreRowModel(),
        state: {
            columnPinning: { left: ['customer_name'] },
        },
    });

    const ordersTable = useReactTable({
        data: ordersData,
        columns: ordersColumns,
        getCoreRowModel: getCoreRowModel(),
        state: {
            columnPinning: { left: ['customer_name'] },
        },
    });

    /* ============================================
     * Infinite scrolls
     * ============================================
     */

    if (isLoadingDetail || !productDetails) {
        return <LoadingBlockFullPage className="mt-64" />;
    }

    const title = `${erpProductId} ${productDetails.product_variant_name}`;

    const canReadProductAvailability = typeof productDetails.total_availability === 'number';

    const tabs = [
        PageTabs.INFO,
        ...(canReadProductAvailability ? [PageTabs.AVAILABILITY] : []),
        ...(canReadOpenOrders ? [PageTabs.OPEN_ORDERS] : []),
        ...(canReadQuotes ? [PageTabs.OPEN_QUOTES] : []),
        PageTabs.EXPLORER,
    ];

    return (
        <DrawerContainer className={className}>
            <div className="flex flex-col gap-3 bg-white">
                <DrawerHeader
                    className="border-b-0 pb-0"
                    title={title}
                    onBack={onBack}
                    onClose={onClose}
                    actions={
                        <>
                            <CopyButton
                                textToCopy={erpProductId}
                                title={t('copy', {
                                    ns: 'common',
                                })}
                            />
                            <InventoryActions itemTitle={title} customerId={customerId} />
                        </>
                    }
                />
                <TabsHeader<PageTabs>
                    className="px-4"
                    selectedTabKey={selectedTab}
                    onTabChange={(value) => {
                        setSelectedTab(value);
                    }}
                    tabs={tabs}
                    getTabLabel={(tab) => t(`tab_title__${tab}`, { ns: 'product-details' })}
                />
            </div>
            <DrawerContent>
                {selectedTab === PageTabs.INFO && (
                    <>
                        <InventoryDetailsInfo data={productDetails} />
                        {enablePricingDiscovery &&
                            (isLoadingPricingSummary || !pricingSummary ? (
                                <CollapsibleContainer
                                    title={t('pricing_history__title', { ns: 'product-details' })}
                                    defaultOpen
                                >
                                    <Card>
                                        <div className="h-12 w-full animate-pulse rounded bg-neutral-50" />
                                    </Card>
                                </CollapsibleContainer>
                            ) : (
                                <InventoryDetailsPricingHistory
                                    productVariantId={erpProductId}
                                    customerId={customerId}
                                    data={pricingSummary}
                                />
                            ))}
                        <InventoryDetailsDescription data={productDetails} />
                        <InventoryDetailsRecommendations data={productDetails} />
                        {canReadProductVariants && (
                            <InventoryDetailsVariants
                                erpProductId={erpProductId}
                                fetchEnabled={isFetchedDetail}
                            />
                        )}
                    </>
                )}
                {selectedTab === PageTabs.AVAILABILITY && (
                    <InventoryDetailsAvailability erpProductId={erpProductId} />
                )}
                {selectedTab === PageTabs.OPEN_ORDERS && (
                    <>
                        <DataTable
                            isLoading={isLoadingOrders}
                            isFetchingNextPage={isFetchingOrdersNextPage}
                            onFetchNextPage={fetchNextPageOrders}
                            hasNextPage={hasNextPageOrders}
                            totalRowsCount={ordersCount}
                            table={ordersTable}
                            columns={ordersColumns}
                        />
                        <TableHeader count={ordersCount} isLoadingCount={isLoadingOrdersCount} />
                    </>
                )}
                {selectedTab === PageTabs.OPEN_QUOTES && (
                    <>
                        <DataTable
                            isLoading={isLoadingQuotes}
                            isFetchingNextPage={isFetchingQuotesNextPage}
                            onFetchNextPage={fetchNextPageQuotes}
                            hasNextPage={hasNextPageQuotes}
                            totalRowsCount={quotesCount}
                            table={quotesTable}
                            columns={quotesColumns}
                        />
                        <TableHeader count={quotesCount} isLoadingCount={isLoadingQuotesCount} />
                    </>
                )}
                {selectedTab === PageTabs.EXPLORER && (
                    <ExplorerViewProvider
                        value={{
                            state,
                            setState,
                            defaultFilters: {
                                product: [erpProductId],
                            },
                        }}
                    >
                        <ExplorerView pageSource="product" />
                    </ExplorerViewProvider>
                )}
            </DrawerContent>
        </DrawerContainer>
    );
};

export default InventoryDetails;
